import { useState, useEffect } from 'react'
import {
  Video,
  Calendar,
  Clock,
  Play,
  Pause,
  Search,
  ChevronDown,
  ChevronUp,
  User,
  FileText,
} from 'lucide-react'
import { evaluationsApi } from '../../lib/api/evaluations'
import { interviewerApi } from '../../lib/api/interviewer'

interface Recording {
  id: number
  title: string
  client_name: string
  client_email?: string
  scheduled_at: string
  duration_minutes: number
  status: string
  recording_url?: string
}

export default function InterviewerRecordings() {
  const [recordings, setRecordings] = useState<Recording[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedRecording, setExpandedRecording] = useState<number | null>(null)
  const [playingId, setPlayingId] = useState<number | null>(null)

  useEffect(() => {
    const fetchRecordings = async () => {
      try {
        const [recordingsRes, sessionsRes] = await Promise.all([
          evaluationsApi.getRecordings().catch(() => []),
          interviewerApi.getSessions().catch(() => ({ data: [] })),
        ])

        const sessions = sessionsRes.data || sessionsRes || []
        const completed = sessions.filter(
          (s: Recording) => s.status === 'completed' && s.recording_url
        )
        const combined = [...(recordingsRes || []), ...completed]

        // Deduplicate by id
        const seen = new Set<number>()
        const unique = combined.filter((r: Recording) => {
          if (seen.has(r.id)) return false
          seen.add(r.id)
          return true
        })
        setRecordings(unique)
      } catch (err) {
        console.error('Failed to fetch recordings:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchRecordings()
  }, [])

  const filteredRecordings = recordings.filter((r) => {
    const name = r.client_name || ''
    const title = r.title || ''
    return (
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      title.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

  if (loading) {
    return <div className="loading-screen">Loading recordings...</div>
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Session Recordings</h1>
        <p className="page-subtitle">
          Access recordings from completed interview sessions
        </p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16,
          }}
        >
          <Search size={18} style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            className="form-input"
            placeholder="Search recordings by client or title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ border: 'none', boxShadow: 'none' }}
          />
        </div>

        {filteredRecordings.length === 0 ? (
          <div className="empty-state">
            <Video size={48} />
            <h3>No recordings found</h3>
            <p>Session recordings will appear here after completion.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filteredRecordings.map((recording) => (
              <div
                key={recording.id}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    padding: 20,
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    setExpandedRecording(
                      expandedRecording === recording.id ? null : recording.id
                    )
                  }
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: 'rgba(45, 55, 72, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--primary-navy)',
                    }}
                  >
                    <Video size={24} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
                      {recording.client_name || 'Client'}
                    </h4>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      {recording.title}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right', marginRight: 8 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 13,
                        color: 'var(--text-muted)',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Calendar size={14} />
                      {formatDate(recording.scheduled_at)}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 13,
                        color: 'var(--text-muted)',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Clock size={14} />
                      {recording.duration_minutes || 60} min
                    </div>
                  </div>
                  {expandedRecording === recording.id ? (
                    <ChevronUp size={18} />
                  ) : (
                    <ChevronDown size={18} />
                  )}
                </div>

                {expandedRecording === recording.id && (
                  <div
                    style={{
                      padding: '16px 20px',
                      borderTop: '1px solid var(--border)',
                      background: 'var(--accent-light)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        marginBottom: 16,
                      }}
                    >
                      {recording.client_email && (
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 13,
                            color: 'var(--text-muted)',
                          }}
                        >
                          <User size={14} />
                          {recording.client_email}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {recording.recording_url && (
                        <button
                          className="btn btn-primary"
                          style={{ padding: '8px 16px', fontSize: 13 }}
                          onClick={(e) => {
                            e.stopPropagation()
                            setPlayingId(
                              playingId === recording.id ? null : recording.id
                            )
                          }}
                        >
                          {playingId === recording.id ? (
                            <>
                              <Pause size={14} /> Pause
                            </>
                          ) : (
                            <>
                              <Play size={14} /> Play Recording
                            </>
                          )}
                        </button>
                      )}
                      {!recording.recording_url && (
                        <span
                          style={{
                            fontSize: 13,
                            color: 'var(--text-muted)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <FileText size={14} />
                          Recording not available
                        </span>
                      )}
                    </div>
                    {playingId === recording.id && (
                      <div
                        style={{
                          marginTop: 16,
                          padding: 16,
                          background: 'white',
                          borderRadius: 8,
                          border: '1px solid var(--border)',
                          textAlign: 'center',
                          color: 'var(--text-muted)',
                        }}
                      >
                        <Video size={32} style={{ marginBottom: 8 }} />
                        <p style={{ fontSize: 14 }}>
                          Audio/video player placeholder - recording would play here
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
